import { config } from './config';
import { apiHelpers } from './helpers';
import { Reminder, HolidayTemplate, SyncResult, CreateReminderRequest, UpdateReminderRequest } from '@/types/reminder';

// Get all reminders for a user
export const getReminders = async (userId: number, params?: { is_active?: boolean; reminder_type?: string }) => {
  const searchParams = new URLSearchParams();
  if (params?.is_active !== undefined) {
    searchParams.append('is_active', params.is_active.toString());
  }
  if (params?.reminder_type) {
    searchParams.append('reminder_type', params.reminder_type);
  }
  
  const url = `${config.API_URL}/reminder/${userId}/reminders${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url);

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to fetch reminders');
  }

  return response.json() as Promise<Reminder[]>;
};

// Create a new reminder
export const createReminder = async (userId: number, reminder: CreateReminderRequest) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders`;
  const response = await apiHelpers.makeAuthenticatedRequest(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reminder),
  });

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to create reminder');
  }

  return response.json() as Promise<Reminder>;
};

// Update an existing reminder
export const updateReminder = async (userId: number, reminderId: number, updates: UpdateReminderRequest) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders/${reminderId}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to update reminder');
  }

  return response.json() as Promise<Reminder>;
};

// Delete a reminder
export const deleteReminder = async (userId: number, reminderId: number) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders/${reminderId}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to delete reminder');
  }

  return response.json() as Promise<{ success: boolean }>;
};

// Get upcoming reminders
export const getUpcomingReminders = async (userId: number, days: number = 30) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders/upcoming?days=${days}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url);

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to fetch upcoming reminders');
  }

  return response.json() as Promise<Reminder[]>;
};

// Sync birthday reminders from friends
export const syncBirthdayReminders = async (userId: number) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders/sync-birthdays`;
  const response = await apiHelpers.makeAuthenticatedRequest(url, {
    method: 'POST',
  });

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to sync birthday reminders');
  }

  return response.json() as Promise<SyncResult>;
};

// Get holiday templates
export const getHolidayTemplates = async (userId: number, countryCode: string = 'DE') => {
  const url = `${config.API_URL}/reminder/${userId}/holidays?country_code=${countryCode}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url);

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to fetch holiday templates');
  }

  return response.json() as Promise<HolidayTemplate[]>;
};

// Create reminder from holiday template
export const createReminderFromHoliday = async (userId: number, holidayId: number, advanceNotice: number = 1) => {
  const url = `${config.API_URL}/reminder/${userId}/reminders/create-from-holiday/${holidayId}?advance_notice=${advanceNotice}`;
  const response = await apiHelpers.makeAuthenticatedRequest(url, {
    method: 'POST',
  });

  if (!response.ok) {
    await apiHelpers.handleApiError(response, 'Failed to create reminder from holiday');
  }

  return response.json() as Promise<Reminder>;
};